/**
 * @class SoulogColorSwitch
 * Color-switch showcase (sections/soulog-color-switch.liquid).
 *
 * Selected swatch → the matching color's cover layer / carousel / model gets .is-on (or .is-active); CSS cross-fades.
 * 3D mode: model-viewer (jsdelivr, pinned version) and the model load only once near the viewport. Two delivery modes:
 *   · One GLB per color: one stacked <model-viewer data-index="i"> per color. Load the current color first, then preload the rest in turn;
 *     on switch, sync the angle the previous one was dragged to onto the next, then cross-fade — the device doesn't jump back to the initial angle.
 *     If the target color hasn't loaded yet, show its image layer first and swap to the model automatically once loaded.
 *   · One GLB containing all colors: a single <model-viewer data-index="all">; switch variantName.
 *     When the link is left blank, use the built-in product model (injected as a classic script; see the header comment of soulog-color-switch-model.js).
 *   Until loaded, the card shows that color's first angle image as the cover.
 *   Slow network: data saver / 2g never loads 3D; not ready MODEL_TIMEOUT seconds after the card is visible → falls back to the image carousel for good.
 *   Once loaded, play the intro animation once (playIntro; keyframes in math.INTRO, not baked into the GLB); it stops as soon as the user drags.
 *   Trackball (setupFaces): fixed camera, rotate the model itself, so it can keep flipping up/down/left/right; on release it snaps with inertia to the nearest stop (math.buildStops).
 * Multi-angle image carousel (display mode = images / 3D failed / 3D off on mobile; visibility decided by CSS from the root node's classes):
 *   one shared <soulog-carousel> per color, stacked; on color change toggle .is-on and have the new color land on the same angle (slide index) as the previous color.
 * Selecting a color block in the theme editor → switch straight to that color.
 */
if (!customElements.get("soulog-color-switch")) {
	// Pinned to 4.2.0: the 4.3.0 / 4.3.1 release bundles accidentally shipped debug console.log (floods the console on every src change); grep for it before upgrading
	const MODEL_VIEWER_SRC = "https://cdn.jsdelivr.net/npm/@google/model-viewer@4.2.0/dist/model-viewer.min.js";
	// model-viewer 4.2.0's default Draco decoder location (it only fetches it after the GLB has been parsed). Must stay byte-identical to that default
	// so our prefetch lands in the same HTTP cache entry; re-check it when upgrading model-viewer
	const DRACO_BASE = "https://www.gstatic.com/draco/versioned/decoders/1.5.6/";
	// Start loading about two screens ahead: on slow networks the old 300px meant the download only began once the user had already arrived
	const PRELOAD_MARGIN = "200% 0px";
	const prefetchDraco = () => {
		for (const f of ["draco_wasm_wrapper.js", "draco_decoder.wasm"]) fetch(DRACO_BASE + f).catch(() => {});
	};

	/** Pure functions, called directly by unit tests (tests/unit/soulog-color-switch.test.mjs) */
	const math = {
		/**
		 * Variant name to switch to when the index-th color is selected.
		 * Use the name if it matches; if not a single name in the whole model matches (usually not yet named per convention),
		 * cycle through them in order so switching colors at least does something, and the caller warns in the console.
		 */
		variantFor(wanted, index, available) {
			if (!available || available.length === 0) return null;
			if (available.includes(wanted[index])) return wanted[index];
			const anyMatch = wanted.some((w) => available.includes(w));
			return anyMatch ? null : available[index % available.length];
		},
		/** Previous color on slide i → which slide the new color lands on: the last slide if there aren't enough */
		clampIndex(i, count) {
			if (count <= 0) return 0;
			return Math.min(Math.max(i, 0), count - 1);
		},
		/** Previous model's camera angle → next model's camera-orbit. Distance stays auto: when every color's model is the same size, auto framing stays consistent */
		orbitFrom(orbit) {
			return orbit.theta + "rad " + orbit.phi + "rad auto";
		},

		/**
		 * Intro animation keyframes, time in seconds. The animation isn't baked into the GLB — swapping models / adding colors needs no re-export by design, and pacing is tuned only here.
		 * Format copied from the apple.com/iphone-18-pro intro timeline ({start, end, from/to values, bezier easing}).
		 * Motion (2026-09-26, per reference video 1.mp4): the device springs up from below the card while scaled down, overshoots slightly, then settles;
		 * after settling it scales up to full size, turns to one side to show its profile, holds a beat, then turns back to the front. The toggle-switch animation in the reference video is not implemented.
		 *   rise: vertical offset, unit = % of card height (positive = below), via CSS translateY; the card's overflow:hidden does the clipping.
		 *         The start must be ≥ 100: the device spans about 11%–89% of card height; if not pushed far enough its top peeks over the bottom edge and looks stuck while waiting to play
		 *   scale: CSS scale, 1 = full size. Camera distance is not changed: distance is auto-framed, and changing it would fight with window resizing
		 *   theta: horizontal offset (degrees) relative to the section's "3D initial view". No pitch track: the intro only rotates horizontally, ending exactly on the fixed "front" face
		 * Ending at scale 1 and everything else 0 = resting at the initial view, original position, full size.
		 */
		INTRO: {
			rise: [
				{ start: 0, end: 0.4, from: 100, to: -5, ease: [0.2, 0.8, 0.3, 1] },
				{ start: 0.4, end: 0.6, from: -5, to: 0, ease: [0.4, 0, 0.4, 1] },
			],
			scale: [
				{ start: 0, end: 0.6, from: 0.6, to: 0.6, ease: [0, 0, 1, 1] },
				{ start: 0.6, end: 1.4, from: 0.6, to: 1, ease: [0.3, 0, 0.15, 1] },
			],
			// Start turning as the scale-up finishes (1.1s) so the transition has no pause
			theta: [
				{ start: 0, end: 1.1, from: 0, to: 0, ease: [0, 0, 1, 1] },
				{ start: 1.1, end: 2.3, from: 0, to: -35, ease: [0.45, 0, 0.2, 1] },
				{ start: 2.3, end: 2.6, from: -35, to: -35, ease: [0, 0, 1, 1] },
				{ start: 2.6, end: 3.5, from: -35, to: 0, ease: [0.45, 0, 0.3, 1] },
			],
		},

		/**
		 * Trackball: fixed camera, rotate the model itself (model-viewer's orientation attribute), with no end in any direction.
		 * Why not rotate the camera: the camera is on a spherical orbit, so going up/down it hits a wall at the pole and can't flip over (both user screen recordings on 2026-09-26 got stuck here).
		 * Orientation is stored as a quaternion [x, y, z, w] and converted to model-viewer's Euler(pitch, yaw, roll, 'YXZ') when written to orientation.
		 * On release it snaps to a stop (buildStops): the cube's 24 axis-aligned orientations + the 45° diagonal side views when upright.
		 * DRAG_SPEED: how many turns a drag across the full card width produces (0.5 = half a turn).
		 */
		DRAG_SPEED: 0.5,

		qMul(a, b) {
			const [ax, ay, az, aw] = a;
			const [bx, by, bz, bw] = b;
			return [
				aw * bx + ax * bw + ay * bz - az * by,
				aw * by - ax * bz + ay * bw + az * bx,
				aw * bz + ax * by - ay * bx + az * bw,
				aw * bw - ax * bx - ay * by - az * bz,
			];
		},
		qInv([x, y, z, w]) {
			return [-x, -y, -z, w];
		},
		qAxisAngle(axis, angle) {
			const len = Math.hypot(...axis);
			if (len < 1e-12 || angle === 0) return [0, 0, 0, 1];
			const s = Math.sin(angle / 2) / len;
			return [axis[0] * s, axis[1] * s, axis[2] * s, Math.cos(angle / 2)];
		},
		/** Rotation angle 0–π and unit axis (taking the shorter path) */
		qToAxisAngle(q) {
			const [x, y, z, w] = q[3] < 0 ? q.map((c) => -c) : q;
			const angle = 2 * Math.acos(Math.min(1, w));
			const s = Math.sqrt(Math.max(0, 1 - w * w));
			return { axis: s < 1e-9 ? [0, 0, 0] : [x / s, y / s, z / s], angle };
		},
		/** Angular difference between two orientations, in radians */
		qAngle(a, b) {
			const dot = Math.abs(a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3]);
			return 2 * Math.acos(Math.min(1, dot));
		},
		qSlerp(a, b, t) {
			let dot = a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3];
			const bb = dot < 0 ? b.map((c) => -c) : b;
			dot = Math.abs(dot);
			if (dot > 0.9995) {
				const q = a.map((c, i) => c + (bb[i] - c) * t);
				const len = Math.hypot(...q);
				return q.map((c) => c / len);
			}
			const th = Math.acos(dot);
			const sa = Math.sin((1 - t) * th) / Math.sin(th);
			const sb = Math.sin(t * th) / Math.sin(th);
			return a.map((c, i) => c * sa + bb[i] * sb);
		},
		qRotate(q, v) {
			const r = math.qMul(math.qMul(q, [v[0], v[1], v[2], 0]), math.qInv(q));
			return [r[0], r[1], r[2]];
		},
		/** Same as three.js Euler(x, y, z, 'YXZ'): R = Ry · Rx · Rz */
		qFromEulerYXZ(x, y, z) {
			return math.qMul(
				math.qMul(math.qAxisAngle([0, 1, 0], y), math.qAxisAngle([1, 0, 0], x)),
				math.qAxisAngle([0, 0, 1], z),
			);
		},
		/** Quaternion → orientation attribute value "roll pitch yaw" (model-viewer internally uses Euler(pitch, yaw, roll, 'YXZ')) */
		orientationOf(q) {
			const [x, y, z, w] = q;
			const m11 = 1 - 2 * (y * y + z * z);
			const m13 = 2 * (x * z + w * y);
			const m21 = 2 * (x * y + w * z);
			const m22 = 1 - 2 * (x * x + z * z);
			const m23 = 2 * (y * z - w * x);
			const m31 = 2 * (x * z - w * y);
			const m33 = 1 - 2 * (x * x + y * y);
			const pitch = Math.asin(-Math.max(-1, Math.min(1, m23)));
			let yaw;
			let roll;
			if (Math.abs(m23) < 0.9999999) {
				yaw = Math.atan2(m13, m33);
				roll = Math.atan2(m21, m22);
			} else {
				yaw = Math.atan2(-m31, m11);
				roll = 0;
			}
			const f = (n) => n.toFixed(6) + "rad";
			return f(roll) + " " + f(pitch) + " " + f(yaw);
		},

		/** Screen right / up / toward-camera directions (world coordinates) with the camera at (theta, phi) */
		viewBasis(theta, phi) {
			const forward = [Math.sin(phi) * Math.sin(theta), Math.cos(phi), Math.sin(phi) * Math.cos(theta)];
			const r = [forward[2], 0, -forward[0]];
			const rl = Math.hypot(...r) || 1;
			const right = r.map((c) => c / rl);
			const up = [
				forward[1] * right[2] - forward[2] * right[1],
				forward[2] * right[0] - forward[0] * right[2],
				forward[0] * right[1] - forward[1] * right[0],
			];
			return { right, up, forward };
		},

		/** Dragged (dx, dy) pixels on screen → world rotation to apply to the model: dragging right rotates about the "up" axis, dragging down about the "right" axis; k = radians per pixel */
		dragRotation(dx, dy, basis, k) {
			const axis = basis.right.map((c, i) => c * dy + basis.up[i] * dx);
			return math.qAxisAngle(axis, k * Math.hypot(dx, dy));
		},

		/**
		 * Stops: the cube's 24 axis-aligned orientations (4 rotations each for front / back / sides / top / bottom, including lying on its side and upside down) + 4 45° diagonal side views when upright.
		 * Why not keep only the "upright" ones: with the top facing the camera, dragging right has no upright stop ahead, so it can only jump to a far-away stop, which looks like random jumping
		 * (measured in the browser on 2026-09-26). With the full set, dragging in any direction reaches the next stop within 90°, just like turning the real object in your hand.
		 */
		buildStops(basis) {
			const stops = [];
			const quarter = [0, 1, 2, 3].map((k) => (k * Math.PI) / 2);
			const add = (q) => {
				if (!stops.some((s) => math.qAngle(s, q) < 1e-6)) stops.push(q);
			};
			for (const a of quarter) {
				for (const b of quarter) {
					for (const c of quarter) {
						add(
							math.qMul(
								math.qMul(math.qAxisAngle(basis.up, a), math.qAxisAngle(basis.right, b)),
								math.qAxisAngle(basis.forward, c),
							),
						);
					}
				}
			}
			for (const a of quarter) add(math.qAxisAngle(basis.up, a + Math.PI / 4));
			return stops;
		},

		/** Threshold (radians) for snapping in the drag direction on release: if this drag exceeds it, continue to the next stop in that direction */
		SNAP_THRESHOLD: (10 * Math.PI) / 180,

		/**
		 * Picking a stop: past the threshold → choose, among stops "along the drag direction" only, the one nearest the predicted landing point proj (no need to drag past halfway to flip);
		 * too small a drag / arrow keys → the stop nearest proj among all stops.
		 */
		pickStop(cur, proj, dragAxis, dragAngle, stops) {
			const nearest = (list) => list.reduce((best, s) => (math.qAngle(s, proj) < math.qAngle(best, proj) ? s : best));
			if (dragAxis && dragAngle >= math.SNAP_THRESHOLD) {
				const ahead = stops.filter((s) => {
					const { axis, angle } = math.qToAxisAngle(math.qMul(s, math.qInv(cur)));
					const along = axis[0] * dragAxis[0] + axis[1] * dragAxis[1] + axis[2] * dragAxis[2];
					return angle < 1e-6 || along > 0.5;
				});
				if (ahead.length) return nearest(ahead);
			}
			return nearest(stops);
		},

		/** Inertial landing point: a spring carrying angular velocity omega (world coordinates, rad/s) naturally turns a further |omega|/SNAP_OMEGA; pick a stop near there */
		glideTo(cur, omega, dragAxis, dragAngle, stops, snapOmega) {
			const speed = Math.hypot(...omega);
			const proj = math.qMul(math.qAxisAngle(omega, speed / snapOmega), cur);
			return math.pickStop(cur, proj, dragAxis, dragAngle, stops);
		},

		/**
		 * Release feel (derived from frame-by-frame diffs of three screen recordings on 2026-09-26; don't regress):
		 *   · Fixed-duration easing starting from zero velocity → on release it stops dead then restarts: "stuck"
		 *   · Dragging fast and releasing near a stop, with a few degrees left to brake from high speed → lurches then crawls: "flicker"
		 * Approach = inertial landing point (same as iOS scroll snapping): from the release velocity, estimate where it would naturally end up (glideTo), then pick a stop near there;
		 * a critically damped spring starts with the release velocity (springAt) → velocity stays continuous throughout, with no bounce-back.
		 * Larger SNAP_OMEGA = faster and shorter glide: 7 ≈ settles into the stop in 0.7s.
		 */
		SNAP_OMEGA: 7,

		/** Critically damped spring: position at time t seconds, starting from x0 with initial velocity v0 (per second) */
		springAt(x0, v0, target, omega, t) {
			const d = x0 - target;
			return target + (d + (v0 + omega * d) * t) * Math.exp(-omega * t);
		},

		/** Fallback: when the initial velocity exceeds omega·distance the spring overshoots the target and bounces back, so cap it */
		clampVelocity(v, x0, target, omega) {
			const cap = omega * Math.abs(target - x0);
			return Math.max(-cap, Math.min(cap, v));
		},

		/** Release velocity (per second): taken from drag samples {t ms, v value} within the 80ms before release; no movement during that window = the hand had stopped = 0 */
		releaseVelocity(samples, now) {
			const recent = samples.filter((s) => now - s.t <= 80);
			if (recent.length < 2) return 0;
			const a = recent[0];
			const b = recent[recent.length - 1];
			return b.t > a.t ? ((b.v - a.v) * 1000) / (b.t - a.t) : 0;
		},

		/** How long (seconds) to wait after the card is in place before playing. Lowered from 0.6 to 0.1 on 2026-09-26: with a longer wait the user has already scrolled past and never sees the intro */
		INTRO_DELAY: 0.1,

		/**
		 * Seconds after the card enters the viewport before giving up on 3D and falling back to the image carousel (added 2026-09-28).
		 * With the intro on, the cover image is hidden until the model is ready, so on a slow network the card sat blank indefinitely.
		 */
		MODEL_TIMEOUT: 4,

		/**
		 * Skip 3D altogether on data saver / 2g (navigator.connection; browsers without it — Safari, Firefox — load as usual).
		 * 3g is NOT treated as slow: Chrome estimates effectiveType from RTT, and from China to an overseas CDN a fast broadband
		 * line is routinely reported as "3g" — gating on it meant 3D never loaded for those visitors. The timeout still covers truly slow links.
		 */
		slowConnection(conn) {
			if (!conn) return false;
			return Boolean(conn.saveData) || /(^|-)2g$/.test(conn.effectiveType || "");
		},

		/**
		 * Whether the card counts as "in place": visible height ≥ 50% of the card (or the viewport, whichever is smaller).
		 * Lowered from 80% to 50% on 2026-09-26: by 80% the card has nearly scrolled to the top before the device starts springing up, so the user doesn't see it.
		 * Use the smaller of the two — when the card is taller than the viewport, measuring against the card could never be reached.
		 */
		introInView(visibleH, cardH, viewportH) {
			const need = Math.min(cardH, viewportH) * 0.5;
			return need > 0 && visibleH >= need;
		},

		introDuration(intro) {
			return Math.max(...Object.values(intro).map((track) => track[track.length - 1].end));
		},

		/** Same as CSS cubic-bezier(x1,y1,x2,y2): given progress x (0–1), solve for y. Newton iteration with bisection fallback */
		cubicBezier([x1, y1, x2, y2], x) {
			if (x <= 0) return 0;
			if (x >= 1) return 1;
			const bez = (a, b, t) => 3 * a * t * (1 - t) ** 2 + 3 * b * t * t * (1 - t) + t ** 3;
			let lo = 0;
			let hi = 1;
			let t = x;
			for (let i = 0; i < 20; i++) {
				const d = bez(x1, x2, t) - x;
				if (Math.abs(d) < 1e-6) break;
				if (d > 0) hi = t;
				else lo = t;
				t = (lo + hi) / 2;
			}
			return bez(y1, y2, t);
		},

		/** A track's value at time t seconds: the first value before it starts, the last value after it ends */
		sampleTrack(track, t) {
			if (t <= track[0].start) return track[0].from;
			for (const seg of track) {
				if (t <= seg.end) {
					const p = (t - seg.start) / (seg.end - seg.start);
					return seg.from + (seg.to - seg.from) * math.cubicBezier(seg.ease, p);
				}
			}
			return track[track.length - 1].to;
		},
	};
	globalThis.SoulogColorSwitchMath = math;

	/** A script with the same URL is injected only once per page */
	const scripts = new Map();
	const loadScript = (src, type) => {
		if (!scripts.has(src)) {
			scripts.set(
				src,
				new Promise((resolve, reject) => {
					const el = document.createElement("script");
					if (type) el.type = type;
					el.src = src;
					el.onload = resolve;
					el.onerror = reject;
					document.head.appendChild(el);
				}),
			);
		}
		return scripts.get(src);
	};

	/**
	 * model-viewer 4.2.0 bug (still unfixed in 4.3.1): changing orientation → updated() calls arRenderer.onUpdateScene();
	 * outside AR, presentedScene is null → `.add` throws a TypeError, and it throws before $needsRender() —
	 * so the orientation changes but the view doesn't re-render (measured in the browser on 2026-09-26). That function only has work to do during AR presentation, so it can simply be skipped outside AR.
	 * The renderer is a page-wide singleton, obtained via the symbol getter on the element whose description is "renderer"; if it can't be found, do nothing (so this naturally stops applying after an upgrade).
	 */
	const patchOrientationBug = (mv) => {
		let renderer = null;
		for (let o = mv; o && !renderer; o = Object.getPrototypeOf(o)) {
			const sym = Object.getOwnPropertySymbols(o).find((x) => x.description === "renderer");
			if (sym) renderer = mv[sym];
		}
		const ar = renderer && renderer.arRenderer;
		if (!ar || ar._scsPatched || typeof ar.onUpdateScene !== "function") return;
		const original = ar.onUpdateScene;
		ar.onUpdateScene = (...args) => (ar.isPresenting ? original.apply(ar, args) : undefined);
		ar._scsPatched = true;
	};

	class SoulogColorSwitch extends HTMLElement {
		connectedCallback() {
			this.root = this.closest(".scs") || this;
			this.inputs = Array.from(this.querySelectorAll(".scs__input"));
			this.layers = Array.from(this.querySelectorAll(".scs__layer"));
			this.carousels = Array.from(this.querySelectorAll(".scs__carousel"));
			this.nameEl = this.querySelector(".scs__name");
			this.soonEl = this.querySelector(".scs__soon");
			this.models = Array.from(this.querySelectorAll(".scs__model"));
			this.perColor = this.models.length > 0 && this.models[0].dataset.index !== "all";
			this.model = this.models[0] || null;
			this.wanted = this.inputs.map((i) => i.dataset.variant);
			this.warned = false;

			this.onChange = (e) => {
				if (e.target.classList.contains("scs__input")) this.show(Number(e.target.value));
			};
			this.addEventListener("change", this.onChange);

			this.onBlockSelect = (e) => {
				const label = this.querySelector('[data-block-id="' + e.detail.blockId + '"]');
				const input = label && label.querySelector(".scs__input");
				if (!input) return;
				input.checked = true;
				this.show(Number(input.value));
			};
			document.addEventListener("shopify:block:select", this.onBlockSelect);

			if (this.model) this.setupModel();
		}

		disconnectedCallback() {
			this.removeEventListener("change", this.onChange);
			document.removeEventListener("shopify:block:select", this.onBlockSelect);
			if (this.io) this.io.disconnect();
			if (this.seenIo) this.seenIo.disconnect();
			clearTimeout(this.giveUpTimer);
			if (this.stopIntro) this.stopIntro();
			if (this.mq && this.onMq) this.mq.removeEventListener("change", this.onMq);
		}

		current() {
			const i = this.inputs.findIndex((el) => el.checked);
			return i < 0 ? 0 : i;
		}

		show(index) {
			if (this.stopIntro) this.stopIntro();
			this.layers.forEach((layer, i) => {
				const on = i === index;
				layer.classList.toggle("is-on", on);
				if (on) layer.removeAttribute("aria-hidden");
				else layer.setAttribute("aria-hidden", "true");
			});
			this.switchCarousel(index);
			if (this.nameEl && this.inputs[index]) this.nameEl.textContent = this.inputs[index].dataset.name;
			// Colors not yet on sale: show the "coming soon" note (element stays in layout, only visibility toggles)
			if (this.soonEl)
				this.soonEl.classList.toggle("is-on", !!this.inputs[index] && this.inputs[index].dataset.soon !== undefined);
			if (!this.model) return;
			if (this.perColor) {
				this.switchModel(index);
			} else {
				this.applyVariant(index);
			}
		}

		/** On color change the carousel switches too and lands on the same angle (the last slide if there aren't that many) */
		switchCarousel(index) {
			const prev = this.carousels.find((c) => c.classList.contains("is-on"));
			const next = this.carousels[index];
			if (!next || next === prev) return;
			const at = prev && typeof prev.currentIndex === "function" ? prev.currentIndex() : 0;
			this.carousels.forEach((c) => {
				const on = c === next;
				c.classList.toggle("is-on", on);
				c.inert = !on;
				if (on) c.removeAttribute("aria-hidden");
				else c.setAttribute("aria-hidden", "true");
			});
			if (typeof next.stops !== "function") return;
			const stops = next.stops();
			const left = stops[math.clampIndex(at, stops.length)];
			// Jump instantly rather than smooth-scroll: the new color should already be in place as it fades in, without visibly sliding over
			if (left !== undefined) next.track.scrollTo({ left, behavior: "instant" });
		}

		/** The model that should currently be shown; with one file per color, it's that color's model */
		activeModel(index) {
			return this.perColor ? this.models[index] : this.model;
		}

		/** Sync .is-active with "model ready": the image layer is covered only once the current model has loaded */
		sync(index) {
			const active = this.activeModel(index);
			this.models.forEach((mv) => mv.classList.toggle("is-active", mv === active));
			this.root.classList.toggle("is-model-ready", !!active && active.dataset.state === "ready");
		}

		switchModel(index) {
			const prev = this.shown;
			const next = this.models[index];
			// Record the orientation when leaving; if the target color hasn't loaded yet, apply it once it has
			if (prev && prev !== next && prev.dataset.state === "ready") this.lastQ = prev._scsQ;
			this.shown = next;
			this.matchOrbit(next);
			this.sync(index);
			if (this.modelsReady && !next.dataset.state) {
				this.loadOne(next).then(() => {
					if (this.shown === next) this.matchOrbit(next);
					this.sync(this.current());
				});
			}
		}

		matchOrbit(mv) {
			if (!this.lastQ || mv.dataset.state !== "ready" || !mv._scsFaces) return;
			// We may be mid-snap when leaving, so the new color goes straight to the nearest stop
			mv._scsFaces.set(math.pickStop(this.lastQ, this.lastQ, null, 0, mv._scsFaces.stops));
		}

		/**
		 * Trackball (see the math.DRAG_SPEED / math.SNAP_OMEGA comments for how it works): called once when the model has just loaded and the camera is at the section's configured initial view.
		 * From then on the camera is fixed (camera-controls has been removed from the liquid); dragging rotates the model itself, and mv._scsQ stores the current orientation.
		 */
		setupFaces(mv) {
			patchOrientationBug(mv);
			mv.jumpCameraToGoal();
			const home = mv.getCameraOrbit();
			// Rotation axes are computed at "eye level", ignoring camera pitch: the camera tilts down 6° by default (to avoid the backlit underside below the lid's lower edge);
			// if the axes tilted with it, the device would appear tilted on screen at side stops; rotating about the world vertical axis keeps every stop upright
			const basis = math.viewBasis(home.theta, Math.PI / 2);
			const stops = math.buildStops(basis);
			let raf = 0;
			let pending = 0;
			const set = (q) => {
				mv._scsQ = q;
				mv.orientation = math.orientationOf(q);
			};
			mv._scsFaces = {
				stops,
				set,
				// If the intro was interrupted, the camera stopped midway: convert the camera's horizontal offset into model orientation and return the camera to the initial view, so the picture doesn't jump
				absorbYaw(dTheta) {
					set(math.qMul(math.qAxisAngle(basis.up, -dTheta), mv._scsQ));
				},
			};
			set([0, 0, 0, 1]);

			/** Glide to target with a spring carrying angular velocity omega (world coordinates) */
			const animateTo = (target, omega) => {
				cancelAnimationFrame(raf);
				const from = mv._scsQ;
				const angle = math.qAngle(from, target);
				if (angle < 1e-6) return set(target);
				const { axis } = math.qToAxisAngle(math.qMul(target, math.qInv(from)));
				const along = (omega[0] * axis[0] + omega[1] * axis[1] + omega[2] * axis[2]) / angle;
				const v0 = math.clampVelocity(along, 0, 1, math.SNAP_OMEGA);
				const t0 = performance.now();
				const tick = (now) => {
					const t = Math.max(0, (now - t0) / 1000);
					const x = math.springAt(0, v0, 1, math.SNAP_OMEGA, t);
					const done = t > 2 || Math.abs(1 - x) < 1e-4;
					set(done ? target : math.qSlerp(from, target, x));
					if (!done) raf = requestAnimationFrame(tick);
				};
				raf = requestAnimationFrame(tick);
			};

			let drag = null;
			mv.addEventListener("pointerdown", (e) => {
				if (e.pointerType === "mouse" && e.button !== 0) return;
				if (this.stopIntro) this.stopIntro();
				cancelAnimationFrame(raf);
				// Keep receiving move events even when dragging outside the model; throws if the pointer is no longer valid, which doesn't affect dragging
				try {
					mv.setPointerCapture(e.pointerId);
				} catch (err) {
					/* ignore */
				}
				mv.classList.add("is-dragging");
				drag = {
					id: e.pointerId,
					start: mv._scsQ,
					x: e.clientX,
					y: e.clientY,
					k: (Math.PI * 2 * math.DRAG_SPEED) / (mv.clientWidth || 1),
					samples: [{ t: performance.now(), x: e.clientX, y: e.clientY }],
				};
			});
			mv.addEventListener("pointermove", (e) => {
				if (!drag || e.pointerId !== drag.id) return;
				const r = math.dragRotation(e.clientX - drag.x, e.clientY - drag.y, basis, drag.k);
				drag.x = e.clientX;
				drag.y = e.clientY;
				drag.samples.push({ t: performance.now(), x: e.clientX, y: e.clientY });
				if (drag.samples.length > 12) drag.samples.shift();
				mv._scsQ = math.qMul(r, mv._scsQ);
				// Write orientation only once per frame (every write recomputes the bounding box and shadow)
				if (!pending) {
					pending = requestAnimationFrame(() => {
						pending = 0;
						set(mv._scsQ);
					});
				}
			});
			const end = (e) => {
				if (!drag || e.pointerId !== drag.id) return;
				const d = drag;
				drag = null;
				mv.classList.remove("is-dragging");
				const now = performance.now();
				const vx = math.releaseVelocity(
					d.samples.map((s) => ({ t: s.t, v: s.x })),
					now,
				);
				const vy = math.releaseVelocity(
					d.samples.map((s) => ({ t: s.t, v: s.y })),
					now,
				);
				const omega = basis.right.map((c, i) => (c * vy + basis.up[i] * vx) * d.k);
				const moved = math.qToAxisAngle(math.qMul(mv._scsQ, math.qInv(d.start)));
				const cur = mv._scsQ;
				animateTo(math.glideTo(cur, omega, moved.axis, moved.angle, stops, math.SNAP_OMEGA), omega);
			};
			mv.addEventListener("pointerup", end);
			mv.addEventListener("pointercancel", end);

			// Keyboard: ← → rotate 45°, ↑ ↓ flip to the top / bottom face
			mv.tabIndex = 0;
			mv.addEventListener("keydown", (e) => {
				const keys = {
					ArrowLeft: [basis.up, -Math.PI / 4],
					ArrowRight: [basis.up, Math.PI / 4],
					ArrowUp: [basis.right, -Math.PI / 2],
					ArrowDown: [basis.right, Math.PI / 2],
				};
				if (!keys[e.key]) return;
				e.preventDefault();
				const q = math.qMul(math.qAxisAngle(...keys[e.key]), mv._scsQ);
				animateTo(math.pickStop(q, q, null, 0, stops), [0, 0, 0]);
			});
		}

		/** Load one model; the result is recorded in data-state (ready / error); never throws */
		loadOne(mv, src) {
			const url = src || mv.dataset.src;
			if (!url) return Promise.resolve(false);
			if (mv._scsLoading) return mv._scsLoading;
			mv.dataset.state = "loading";
			mv._scsLoading = new Promise((resolve) => {
				mv.addEventListener(
					"load",
					() => {
						mv.dataset.state = "ready";
						this.setupFaces(mv);
						resolve(true);
					},
					{ once: true },
				);
				mv.addEventListener(
					"error",
					() => {
						mv.dataset.state = "error";
						resolve(false);
					},
					{ once: true },
				);
				mv.src = url;
			});
			return mv._scsLoading;
		}

		/**
		 * Intro animation (keyframes in math.INTRO). As soon as the model loads it's placed in the starting pose (still fading in under the cover image at this point);
		 * playback starts once the card is more than half in the viewport plus INTRO_DELAY — the model starts downloading about two screens early (PRELOAD_MARGIN),
		 * so if it played on viewport entry, it would finish while the user is still scrolling, before their eyes land on the card.
		 * If the card scrolls back out during the wait → cancel, and restart the timer next time it's in place.
		 * Plays only once; as soon as the user presses / drags or switches color, it stops at the current angle and hands control to the user.
		 */
		/** Intro finished / interrupted / won't play: the cover layer goes back to its fallback role (CSS hides it before this) */
		introDone() {
			this.root.classList.add("is-intro-done");
		}

		playIntro(mv) {
			if (this.dataset.modelIntro === "off") return this.introDone();
			if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return this.introDone();

			mv.jumpCameraToGoal();
			const home = mv.cameraOrbit;
			const base = mv.getCameraOrbit();
			const intro = math.INTRO;
			const deg = Math.PI / 180;
			const pose = (t) => {
				mv.style.transform =
					"translateY(" + math.sampleTrack(intro.rise, t) + "%) scale(" + math.sampleTrack(intro.scale, t) + ")";
				mv.cameraOrbit = math.orbitFrom({
					theta: base.theta + math.sampleTrack(intro.theta, t) * deg,
					phi: base.phi,
				});
				mv.jumpCameraToGoal();
			};
			// Promote to a compositing layer ahead of time: otherwise the layer is created on the first translated frame, causing a hitch at the start of the jump
			mv.style.willChange = "transform";
			pose(0);

			let raf = 0;
			let io = null;
			let wait = 0;
			const onCamera = (e) => {
				if (e.detail && e.detail.source === "user-interaction") this.stopIntro();
			};
			const stop = () => {
				cancelAnimationFrame(raf);
				clearTimeout(wait);
				mv.style.transform = "";
				mv.style.willChange = "";
				// Interrupted midway: fold the camera's offset angle into the model orientation and return the camera to the initial view (the trackball assumes a fixed camera)
				const off = mv.getCameraOrbit().theta - base.theta;
				if (Math.abs(off) > 1e-6 && mv._scsFaces) {
					mv.cameraOrbit = home;
					mv.jumpCameraToGoal();
					mv._scsFaces.absorbYaw(off);
				}
				if (io) io.disconnect();
				mv.removeEventListener("pointerdown", stop);
				mv.removeEventListener("camera-change", onCamera);
				this.stopIntro = null;
				this.introDone();
			};
			this.stopIntro = stop;
			mv.addEventListener("pointerdown", stop);
			mv.addEventListener("camera-change", onCamera);

			const duration = math.introDuration(intro);
			const run = () => {
				const t0 = performance.now();
				const tick = (now) => {
					const t = (now - t0) / 1000;
					if (t >= duration) {
						// Restore the section's original configured value (distance auto) so auto framing also works on later window resizes
						mv.cameraOrbit = home;
						mv.jumpCameraToGoal();
						stop();
						return;
					}
					pose(t);
					raf = requestAnimationFrame(tick);
				};
				raf = requestAnimationFrame(tick);
			};
			// "In place" is judged by visible height rather than a ratio threshold: when the card is taller than the viewport the ratio can never be reached (see math.introInView).
			// Observe the card rather than mv: mv is pushed down by translateY, so its own bounding box would give the wrong visible height
			const stage = mv.parentElement;
			const steps = Array.from({ length: 21 }, (_, i) => i / 20);
			io = new IntersectionObserver(
				(entries) => {
					const e = entries[entries.length - 1];
					const viewportH = e.rootBounds ? e.rootBounds.height : window.innerHeight;
					const inView = math.introInView(e.intersectionRect.height, e.boundingClientRect.height, viewportH);
					if (!inView) {
						clearTimeout(wait);
						wait = 0;
						return;
					}
					if (wait) return;
					wait = setTimeout(() => {
						io.disconnect();
						io = null;
						run();
					}, math.INTRO_DELAY * 1000);
				},
				{ threshold: steps },
			);
			io.observe(stage);
		}

		applyVariant(index) {
			const mv = this.model;
			if (!mv || mv.dataset.state !== "ready") return;
			const name = math.variantFor(this.wanted, index, mv.availableVariants);
			if (name) mv.variantName = name;
			if (!this.warned && name && name !== this.wanted[index]) {
				this.warned = true;
				console.warn(
					"[soulog-color-switch] Model variant names don't match the color blocks; mapped by order instead. Variants in the model:",
					mv.availableVariants,
				);
			}
		}

		setupModel() {
			// Phones and tablets don't load 3D by default; it loads only at the desktop tier (same definition as the desktop type tier in soulog-home-type.css; tablets switched to images on 2026-09-26).
			// matchMedia includes a width gate so the editor's mobile preview is matched correctly too (see CLAUDE.md responsive rule 4)
			const allowMobile = this.dataset.modelMobile === "true";
			this.mq = window.matchMedia("(min-width: 1024px) and (orientation: landscape), (min-width: 1367px)");
			const allowed = () => allowMobile || this.mq.matches;
			// Slow network: the images are the better experience, don't even start downloading ~1MB of 3D
			if (math.slowConnection(navigator.connection)) {
				this.root.classList.add("is-model-off");
				return;
			}

			if (!allowed()) {
				this.root.classList.add("is-model-off");
				// Load later if the window is widened to desktop
				this.onMq = () => {
					if (!allowed()) return;
					this.mq.removeEventListener("change", this.onMq);
					this.root.classList.remove("is-model-off");
					this.watch();
				};
				this.mq.addEventListener("change", this.onMq);
				return;
			}
			this.watch();
		}

		/** Start downloading about two screens before entering the viewport (PRELOAD_MARGIN), so visitors who never scroll this far spend no bandwidth */
		watch() {
			this.io = new IntersectionObserver(
				(entries) => {
					if (!entries.some((e) => e.isIntersecting)) return;
					this.io.disconnect();
					this.loadModel();
				},
				{ rootMargin: PRELOAD_MARGIN },
			);
			this.io.observe(this);

			// Timeout clock starts when the card actually becomes visible (what the visitor perceives), not when the preload starts
			this.seenIo = new IntersectionObserver((entries) => {
				if (!entries.some((e) => e.isIntersecting)) return;
				this.seenIo.disconnect();
				this.giveUpTimer = setTimeout(() => {
					if (!this.ready) this.giveUp();
				}, math.MODEL_TIMEOUT * 1000);
			});
			this.seenIo.observe(this);
		}

		/** Fall back to the image carousel for good; a model that arrives late does not take over (would pop in mid-read) */
		giveUp() {
			this.gaveUp = true;
			if (this.stopIntro) this.stopIntro();
			this.root.classList.add("is-model-off");
		}

		async loadModel() {
			const fail = () => this.root.classList.add("is-model-off");
			try {
				// Fetch in parallel rather than one after another: built-in model script, model-viewer, and the Draco decoder
				// (which model-viewer would otherwise only request after parsing the GLB — a third serial round-trip)
				const useDemo = !this.perColor && !this.dataset.modelSrc;
				prefetchDraco();
				await Promise.all([useDemo ? loadScript(this.dataset.demoSrc) : null, loadScript(MODEL_VIEWER_SRC, "module")]);
				const demo = useDemo ? window.SoulogColorSwitchGLB : null;
				await customElements.whenDefined("model-viewer");
				if (this.gaveUp) return;
				this.modelsReady = true;

				if (!this.perColor) {
					this.shown = this.model;
					this.sync(this.current());
					const ok = await this.loadOne(this.model, this.dataset.modelSrc || demo);
					if (this.gaveUp) return;
					if (!ok) return fail();
					this.ready = true;
					this.playIntro(this.model);
					this.sync(this.current());
					this.applyVariant(this.current());
					return;
				}

				// One file per color: current color first, the rest queued for preload, so they're most likely ready when switched to
				const first = this.current();
				this.shown = this.models[first];
				this.sync(first);
				// Even the current color's model fails → the whole block falls back to the multi-angle image carousel (other colors are not attempted)
				const ok = await this.loadOne(this.models[first]);
				if (this.gaveUp) return;
				if (!ok) return fail();
				this.ready = true;
				// Don't play if the user already switched color during loading
				if (this.shown === this.models[first]) this.playIntro(this.models[first]);
				else this.introDone();
				this.sync(this.current());
				for (const mv of this.models) {
					if (mv.dataset.state) continue;
					await this.loadOne(mv);
					if (this.gaveUp) return;
					this.sync(this.current());
				}
			} catch (e) {
				fail();
			}
		}
	}

	customElements.define("soulog-color-switch", SoulogColorSwitch);
}
